import React, { useState } from 'react';
import { X, Loader, CheckCircle, AlertCircle } from 'lucide-react';

const AddMarketplaceModal = ({ client, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [testSuccess, setTestSuccess] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    marketplaceType: 'LEROY_MERLIN',
    apiUrl: 'https://leroymerlinza-prod.mirakl.net',
    apiKey: '',
    shopId: '',
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
    setTestSuccess(false);
  };

  const testConnection = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${window.location.hostname === "localhost" ? "http://localhost:3000" : "https://syncbridge.co.za/api"}/mirakl/test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          apiUrl: formData.apiUrl,
          apiKey: formData.apiKey,
        }),
      });
      const data = await response.json();
      if (data.success) {
        setTestSuccess(true);
      } else {
        setError('Connection failed. Check your API key.');
      }
    } catch (err) {
      setError('Connection failed. Check your API key.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${window.location.hostname === "localhost" ? "http://localhost:3000" : "https://syncbridge.co.za/api"}/mirakl/connections`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tenantId: client.id,
          marketplaceType: formData.marketplaceType,
          marketplaceRegion: 'ZA',
          apiUrl: formData.apiUrl,
          apiKey: formData.apiKey,
          shopId: formData.shopId,
        }),
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.message || 'Failed to connect marketplace');
      }

      onSuccess();
    } catch (err) {
      setError(err.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const canSubmit = testSuccess && formData.shopId;

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={e => e.stopPropagation()}>
        <div style={styles.header}>
          <h2 style={styles.title}>Connect Marketplace</h2>
          <button style={styles.closeBtn} onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div style={styles.body}>
          <div style={styles.field}>
            <label style={styles.label}>Marketplace</label>
            <select
              style={styles.select}
              value={formData.marketplaceType}
              onChange={e => handleChange('marketplaceType', e.target.value)}
            >
              <option value="LEROY_MERLIN">Leroy Merlin</option>
              <option value="MAKRO" disabled>Makro (Coming Soon)</option>
              <option value="TAKEALOT" disabled>Takealot (Coming Soon)</option>
            </select>
          </div>

          <div style={styles.field}>
            <label style={styles.label}>API URL</label>
            <input
              type="text"
              style={styles.input}
              value={formData.apiUrl}
              onChange={e => handleChange('apiUrl', e.target.value)}
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>API Key</label>
            <input
              type="password"
              style={styles.input}
              placeholder="Enter your Mirakl API key"
              value={formData.apiKey}
              onChange={e => handleChange('apiKey', e.target.value)}
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Shop ID</label>
            <input
              type="text"
              style={styles.input}
              placeholder="e.g. 6439"
              value={formData.shopId}
              onChange={e => handleChange('shopId', e.target.value)}
            />
          </div>

          <button
            style={{
              ...styles.testBtn,
              backgroundColor: testSuccess ? '#f0fdf4' : '#f3f4f6',
              color: testSuccess ? '#16a34a' : '#374151',
            }}
            onClick={testConnection}
            disabled={loading || !formData.apiUrl || !formData.apiKey}
          >
            {loading ? (
              <Loader size={16} />
            ) : testSuccess ? (
              <CheckCircle size={16} />
            ) : null}
            {testSuccess ? 'Connected' : 'Test Connection'}
          </button>

          {error && (
            <div style={styles.error}>
              <AlertCircle size={16} />
              {error}
            </div>
          )}
        </div>

        <div style={styles.footer}>
          <button style={styles.cancelBtn} onClick={onClose}>
            Cancel
          </button>
          <button
            style={{ ...styles.primaryBtn, opacity: canSubmit ? 1 : 0.5 }}
            onClick={handleSubmit}
            disabled={!canSubmit || loading}
          >
            {loading ? 'Connecting...' : 'Connect Marketplace'}
          </button>
        </div>
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    width: '100%',
    maxWidth: '440px',
    maxHeight: '90vh',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: '1px solid #e5e7eb',
  },
  title: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#111827',
    margin: 0,
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#6b7280',
  },
  body: {
    padding: '24px',
  },
  field: {
    marginBottom: '16px',
  },
  label: {
    display: 'block',
    fontSize: '14px',
    fontWeight: '500',
    color: '#374151',
    marginBottom: '6px',
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    fontSize: '14px',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    outline: 'none',
    boxSizing: 'border-box',
  },
  select: {
    width: '100%',
    padding: '10px 12px',
    fontSize: '14px',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    outline: 'none',
    boxSizing: 'border-box',
    backgroundColor: '#fff',
  },
  testBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    width: '100%',
    padding: '10px',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    marginTop: '8px',
  },
  error: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px',
    backgroundColor: '#fef2f2',
    color: '#dc2626',
    borderRadius: '8px',
    fontSize: '14px',
    marginTop: '16px',
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    padding: '16px 24px',
    borderTop: '1px solid #e5e7eb',
    gap: '12px',
  },
  cancelBtn: {
    padding: '10px 16px',
    backgroundColor: '#f3f4f6',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    color: '#374151',
    cursor: 'pointer',
  },
  primaryBtn: {
    padding: '10px 20px',
    backgroundColor: '#3b82f6',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
  },
};

export default AddMarketplaceModal;

